import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { removeJournalImage, uploadJournalImage } from "../lib/bdayStorage";
import {
  BdayAppState,
  BdayRow,
  BDAY_SESSION_ID,
  EMPTY_BDAY_STATE,
  isSupabaseConfigured,
  JournalEntry,
  normalizeJournalEntry,
  rowToState,
  stateToRow,
  supabase,
} from "../lib/supabase";

type BdayStateContextValue = {
  ready: boolean;
  syncing: boolean;
  syncError: string | null;
  online: boolean;
  timeline: Record<string, boolean>;
  toggleTimeline: (id: string) => void;
  getChecklist: (key: string) => Record<string, boolean>;
  toggleChecklistItem: (key: string, item: string) => void;
  getShoppingItems: (key: string) => string[];
  getShoppingRemoved: (key: string) => string[];
  addShoppingItem: (key: string, item: string) => void;
  removeShoppingItem: (key: string, item: string) => void;
  dateChoice: string | null;
  setDateChoice: (choice: string | null) => void;
  getJournal: (day: string) => JournalEntry;
  setJournalText: (day: string, text: string) => void;
  uploadJournalPhoto: (day: string, file: File) => Promise<void>;
  removeJournalPhoto: (day: string, imageUrl: string) => Promise<void>;
  notes: string;
  setNotes: (notes: string) => void;
};

const BdayStateContext = createContext<BdayStateContextValue | null>(null);

function migrateFromLocalStorage(): BdayAppState {
  if (typeof window === "undefined") return EMPTY_BDAY_STATE;

  const state: BdayAppState = {
    timeline: {},
    checklists: {},
    dateChoice: null,
    journals: {},
    shoppingItems: {},
    shoppingRemoved: {},
    notes: "",
  };

  try {
    const timelineRaw = window.localStorage.getItem("bday-timeline");
    if (timelineRaw) state.timeline = JSON.parse(timelineRaw);

    const notesRaw = window.localStorage.getItem("bday-notes");
    if (notesRaw) state.notes = JSON.parse(notesRaw);

    const dateRaw = window.localStorage.getItem("bday-date-choice");
    if (dateRaw) state.dateChoice = JSON.parse(dateRaw);

    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (!key?.startsWith("bday-")) continue;

      if (key.startsWith("bday-journal-")) {
        const day = key.replace("bday-journal-", "");
        const raw = window.localStorage.getItem(key);
        if (raw) state.journals[day] = normalizeJournalEntry(JSON.parse(raw));
        continue;
      }

      if (key === "bday-timeline" || key === "bday-notes" || key === "bday-date-choice") {
        continue;
      }

      const checklistKey = key.replace(/^bday-/, "");
      const raw = window.localStorage.getItem(key);
      if (raw) state.checklists[checklistKey] = JSON.parse(raw);
    }
  } catch {
    return EMPTY_BDAY_STATE;
  }

  return state;
}

function hasAnyData(state: BdayAppState) {
  return (
    Object.keys(state.timeline).length > 0 ||
    Object.keys(state.checklists).length > 0 ||
    Object.keys(state.journals).length > 0 ||
    Object.keys(state.shoppingItems).length > 0 ||
    Object.keys(state.shoppingRemoved).length > 0 ||
    state.dateChoice !== null ||
    state.notes.trim().length > 0
  );
}

export function BdayStateProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<BdayAppState>(EMPTY_BDAY_STATE);
  const [ready, setReady] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [online, setOnline] = useState(isSupabaseConfigured);

  const stateRef = useRef(state);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const remoteUpdateRef = useRef(false);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const persist = useCallback(async (next: BdayAppState) => {
    if (!supabase) return;

    setSyncing(true);
    setSyncError(null);

    const { error } = await supabase
      .from("bday_state")
      .upsert(stateToRow(next), { onConflict: "session_id" });

    if (error) {
      setSyncError(error.message);
      setOnline(false);
    } else {
      setOnline(true);
    }

    setSyncing(false);
  }, []);

  const scheduleSave = useCallback(
    (next: BdayAppState, delay = 400) => {
      if (!supabase) return;

      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        void persist(next);
      }, delay);
    },
    [persist]
  );

  const updateState = useCallback(
    (updater: (prev: BdayAppState) => BdayAppState, delay = 400) => {
      setState((prev) => {
        const next = updater(prev);
        stateRef.current = next;
        if (!remoteUpdateRef.current) scheduleSave(next, delay);
        return next;
      });
    },
    [scheduleSave]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!supabase) {
        const local = migrateFromLocalStorage();
        if (!cancelled) {
          setState(local);
          setReady(true);
          setOnline(false);
          setSyncError("Supabase is not configured. Add REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY to .env");
        }
        return;
      }

      const { data, error } = await supabase
        .from("bday_state")
        .select("*")
        .eq("session_id", BDAY_SESSION_ID)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        setSyncError(error.message);
        setState(migrateFromLocalStorage());
        setOnline(false);
        setReady(true);
        return;
      }

      if (data) {
        const remote = rowToState(data as BdayRow);
        const local = migrateFromLocalStorage();

        if (!hasAnyData(remote) && hasAnyData(local)) {
          setState(local);
          await persist(local);
        } else {
          setState(remote);
        }
      } else {
        const local = migrateFromLocalStorage();
        const initial = hasAnyData(local) ? local : EMPTY_BDAY_STATE;
        setState(initial);
        await persist(initial);
      }

      setOnline(true);
      setReady(true);
    }

    void load();

    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, [persist]);

  useEffect(() => {
    if (!supabase) return;

    const channel = supabase
      .channel("bday-state-sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "bday_state", filter: `session_id=eq.${BDAY_SESSION_ID}` },
        (payload) => {
          const row = (payload.new ?? payload.old) as BdayRow | null;
          if (!row) return;

          remoteUpdateRef.current = true;
          setState(rowToState(row));
          remoteUpdateRef.current = false;
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, []);

  const value = useMemo<BdayStateContextValue>(
    () => ({
      ready,
      syncing,
      syncError,
      online,
      timeline: state.timeline,
      toggleTimeline: (id) =>
        updateState((prev) => ({
          ...prev,
          timeline: { ...prev.timeline, [id]: !prev.timeline[id] },
        }), 200),
      getChecklist: (key) => state.checklists[key] ?? {},
      toggleChecklistItem: (key, item) =>
        updateState((prev) => {
          const current = prev.checklists[key] ?? {};
          return {
            ...prev,
            checklists: {
              ...prev.checklists,
              [key]: { ...current, [item]: !current[item] },
            },
          };
        }, 200),
      getShoppingItems: (key) => state.shoppingItems[key] ?? [],
      getShoppingRemoved: (key) => state.shoppingRemoved[key] ?? [],
      addShoppingItem: (key, item) => {
        const trimmed = item.trim();
        if (!trimmed) return;

        updateState((prev) => {
          const current = prev.shoppingItems[key] ?? [];
          const removed = (prev.shoppingRemoved[key] ?? []).filter((existing) => existing !== trimmed);
          if (current.some((existing) => existing.toLowerCase() === trimmed.toLowerCase())) {
            return { ...prev, shoppingRemoved: { ...prev.shoppingRemoved, [key]: removed } };
          }
          return {
            ...prev,
            shoppingItems: {
              ...prev.shoppingItems,
              [key]: [...current, trimmed],
            },
            shoppingRemoved: {
              ...prev.shoppingRemoved,
              [key]: removed,
            },
          };
        }, 200);
      },
      removeShoppingItem: (key, item) =>
        updateState((prev) => {
          const current = prev.shoppingItems[key] ?? [];
          const nextCustom = current.filter((existing) => existing !== item);
          const removed = prev.shoppingRemoved[key] ?? [];
          const nextRemoved = removed.includes(item) ? removed : [...removed, item];
          const nextChecklist = { ...(prev.checklists[key] ?? {}) };
          delete nextChecklist[item];

          return {
            ...prev,
            shoppingItems: {
              ...prev.shoppingItems,
              [key]: nextCustom,
            },
            shoppingRemoved: {
              ...prev.shoppingRemoved,
              [key]: nextRemoved,
            },
            checklists: {
              ...prev.checklists,
              [key]: nextChecklist,
            },
          };
        }, 200),
      dateChoice: state.dateChoice,
      setDateChoice: (choice) =>
        updateState((prev) => ({ ...prev, dateChoice: choice }), 200),
      getJournal: (day) => state.journals[day] ?? { text: "", images: [] },
      setJournalText: (day, text) =>
        updateState((prev) => ({
          ...prev,
          journals: {
            ...prev.journals,
            [day]: { ...(prev.journals[day] ?? { text: "", images: [] }), text },
          },
        }), 600),
      uploadJournalPhoto: async (day, file) => {
        const imageUrl = await uploadJournalImage(day, file);
        updateState((prev) => {
          const entry = prev.journals[day] ?? { text: "", images: [] };
          return {
            ...prev,
            journals: {
              ...prev.journals,
              [day]: { ...entry, images: [...entry.images, imageUrl] },
            },
          };
        }, 200);
      },
      removeJournalPhoto: async (day, imageUrl) => {
        await removeJournalImage(imageUrl);
        updateState((prev) => {
          const entry = prev.journals[day] ?? { text: "", images: [] };
          return {
            ...prev,
            journals: {
              ...prev.journals,
              [day]: { ...entry, images: entry.images.filter((url) => url !== imageUrl) },
            },
          };
        }, 200);
      },
      notes: state.notes,
      setNotes: (notes) =>
        updateState((prev) => ({ ...prev, notes }), 600),
    }),
    [ready, syncing, syncError, online, state, updateState]
  );

  if (!ready) {
    return (
      <div className="flex min-h-screen items-center justify-center px-6 text-center text-sm text-charcoal/70">
        Loading your weekend…
      </div>
    );
  }

  return <BdayStateContext.Provider value={value}>{children}</BdayStateContext.Provider>;
}

export function useBdayState() {
  const ctx = useContext(BdayStateContext);
  if (!ctx) throw new Error("useBdayState must be used within BdayStateProvider");
  return ctx;
}

export function useBdayChecklist(key: string, items: string[]) {
  const { getChecklist, toggleChecklistItem } = useBdayState();
  const state = getChecklist(key);
  const completed = items.filter((item) => state[item]).length;

  return {
    state,
    toggle: (item: string) => toggleChecklistItem(key, item),
    completed,
    total: items.length,
  };
}
